/**
 * @module components/Featured
 * @description Brief §4.2: four large, AFJ-picked featured causes in a
 * horizontal carousel with arrows; "why now" copy, urgency badges, CTAs,
 * and a View all causes link.
 *
 * Native scroll-snap does the scrolling (touch/trackpad for free); arrows
 * scroll by one card and disable at either end.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { useCauseActions } from "../app/useCauseActions";
import { CATEGORY_LABELS } from "../config/taxonomy";
import type { Cause } from "../domain/types";
import { scrollToSection } from "./Hero";
import { Icon } from "./Icons";
import { Progress } from "./Progress";
import { UrgencyBadge } from "./CauseCard";
import "./Featured.css";

/** Featured carousel. Renders nothing until causes load. */
export function Featured({ causes }: { causes: Cause[] }): JSX.Element {
    const track = useRef<HTMLDivElement>(null);
    const [atStart, setAtStart] = useState<boolean>(true);
    const [atEnd, setAtEnd] = useState<boolean>(false);
    const { openQuickView } = useGiving();
    const actions = useCauseActions();
    const featured: Cause[] = causes.filter((c: Cause) => c.featured).slice(0, 4);

    // Arrow enable/disable state follows the real scroll position.
    const sync = useCallback((): void => {
        const el: HTMLDivElement | null = track.current;
        if (el === null) return;
        setAtStart(el.scrollLeft < 8);
        setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 8);
    }, []);

    useEffect(() => {
        sync();
        window.addEventListener("resize", sync);
        return () => window.removeEventListener("resize", sync);
    }, [sync, featured.length]);

    /** Scroll by one card width in `direction`. */
    const step = (direction: 1 | -1): void => {
        const el: HTMLDivElement | null = track.current;
        const card: Element | null = el?.firstElementChild ?? null;
        if (el === null || card === null) return;
        el.scrollBy({ left: direction * (card.getBoundingClientRect().width + 24), behavior: "smooth" });
    };

    return (
        <section id="featured" className="section featured" aria-labelledby="featured-title">
            <div className="container">
                <div className="section__head">
                    <div>
                        <p className="kicker">Hand-picked by AFJ</p>
                        <h2 id="featured-title" className="section__title">Featured causes</h2>
                        <p className="section__sub">Priority causes where a gift right now makes the biggest difference.</p>
                    </div>
                    <div className="featured__nav">
                        <button type="button" className="icon-btn" onClick={() => step(-1)} disabled={atStart} aria-label="Previous featured cause"><Icon name="chevron-left" /></button>
                        <button type="button" className="icon-btn" onClick={() => step(1)} disabled={atEnd} aria-label="Next featured cause"><Icon name="chevron-right" /></button>
                        <button type="button" className="btn btn--ghost" onClick={() => scrollToSection("browse")}>View all causes <Icon name="arrow-right" /></button>
                    </div>
                </div>
            </div>

            <div className="featured__track" ref={track} onScroll={sync} tabIndex={0} aria-label="Featured causes, scroll horizontally">
                {featured.map((cause: Cause, i: number) => (
                    <article key={cause.id} className="featured-card" aria-labelledby={`featured-${cause.id}`} style={{ "--stagger-i": i } as CSSProperties}>
                        <img className="featured-card__img" src={cause.image.src} alt={cause.image.alt} loading="lazy" />
                        <div className="featured-card__shade" />
                        <div className="featured-card__body">
                            <div className="featured-card__badges">
                                <UrgencyBadge cause={cause} />
                                <span className="badge">{CATEGORY_LABELS[cause.category]}</span>
                            </div>
                            <p className="featured-card__partner">{cause.partner}</p>
                            <h3 id={`featured-${cause.id}`} className="featured-card__title">{cause.title}</h3>
                            <p className="featured-card__why"><span className="featured-card__why-label">Why now</span> {cause.whyNow}</p>
                            <p className="featured-card__impact"><strong>{cause.impact.value}</strong> {cause.impact.label}</p>
                            <Progress funding={cause.funding} inverse />
                            <div className="featured-card__actions">
                                <button type="button" className="btn btn--gold btn--lg" onClick={() => actions.addToCart(cause)}><Icon name="cart" /> Add to Cart</button>
                                <button type="button" className="btn btn--on-dark" onClick={() => openQuickView(cause.id)}>Quick View</button>
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
}
